/**
 * Time zones (04 §11, REQUESTS #17, HANDOFF §0.11). The API sends UTC instants only.
 *
 * - Screens: the viewer's zone (`GET /me` → `user.timezone`, else the browser's) next to UTC.
 * - Documents (SRP pack, exports, user notice, advisories, reports): the company zone
 *   (`currentTenant.companyTimeZone`), labelled; where a document or its deadline is shown in a different zone,
 *   the screen says so.
 * - Legal deadlines are UTC and never move; only their display changes.
 */

import { formatLocale } from "../i18n/locale";

/** Default company zone when the company profile has none yet (REQUESTS #7). */
export const DEFAULT_COMPANY_ZONE = "Europe/Brussels";

/**
 * An instant from the API (`2026-10-01T12:33:44.892798Z`). ECMAScript only guarantees three fractional digits,
 * so longer fractions are cut to milliseconds before parsing. Invalid input gives an Invalid Date.
 */
export function parseInstant(value: string | Date): Date {
  if (value instanceof Date) return value;
  return new Date(value.replace(/(\.\d{3})\d+/, "$1"));
}

/** True when this browser can format in the zone (any id `Intl` accepts). */
export function isKnownZone(id: string | null | undefined): id is string {
  if (!id) return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: id });
    return true;
  } catch {
    return false;
  }
}

/**
 * Input validation only (zone pickers, the company profile): IANA region ids, `UTC` or `Area/Location`, known to
 * this browser; offsets, abbreviations and `Etc/GMT±n` (inverted sign) are refused, as the API's documented rule
 * (400 `invalid-timezone`). Zones that come FROM the API are displayed with `isKnownZone` instead: the server
 * accepts any Java ZoneId today (`Etc/UTC`, `GMT`, `Singapore`; REQUESTS #38).
 */
export function isIanaZone(id: string | null | undefined): id is string {
  if (!id) return false;
  if (id !== "UTC" && (!/^[A-Za-z]+(?:\/[A-Za-z0-9_+-]+)+$/.test(id) || id.startsWith("Etc/"))) return false;
  return isKnownZone(id);
}

export function browserZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isKnownZone(zone) ? zone : "UTC";
}

interface MeLike {
  user?: { timezone?: string | null } | null;
  currentTenant?: { companyTimeZone?: string | null } | null;
}

/** The zone screens use for this viewer: the personal zone if set, else the browser's. */
export function viewerZone(me?: MeLike | null): string {
  const own = me?.user?.timezone;
  return isKnownZone(own) ? own : browserZone();
}

/** The zone documents use: the company zone, else the default. */
export function companyZone(me?: MeLike | null): string {
  const zone = me?.currentTenant?.companyTimeZone;
  return isKnownZone(zone) ? zone : DEFAULT_COMPANY_ZONE;
}

export interface FormatOptions {
  /** Include the year (default true). */
  year?: boolean;
  /** Include seconds (default false). */
  seconds?: boolean;
}

/** "8 Oct 2026, 08:40" in the given zone, in the active UI locale's format (L1: "8 paź 2026, 08:40" in pl). */
export function formatInZone(
  instant: string | Date,
  zone: string,
  { year = true, seconds = false }: FormatOptions = {},
): string {
  return new Intl.DateTimeFormat(formatLocale(), {
    timeZone: zone,
    day: "numeric",
    month: "short",
    ...(year ? { year: "numeric" } : {}),
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    hourCycle: "h23",
  }).format(parseInstant(instant));
}

/**
 * The zone's offset from UTC at that instant, in minutes, worked out from the zone's wall clock (not from ICU's zone
 * names: "UTC", "Etc/UTC" and "GMT" are named differently by the ICU builds of macOS and Linux, F19).
 */
export function offsetMinutes(instant: string | Date, zone: string): number {
  const d = parseInstant(instant);
  // Numbers to compute with, not text to show: a fixed locale with Latin digits.
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(d)
      .map((p) => [p.type, Number(p.value)]),
  ) as Record<string, number>;
  const wall = Date.UTC(
    parts.year ?? 0,
    (parts.month ?? 1) - 1,
    parts.day ?? 1,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );
  return Math.round((wall - Math.floor(d.getTime() / 1000) * 1000) / 60_000);
}

/** "UTC+2" / "UTC" / "UTC+5:30" / "UTC-3": the zone's offset at that instant. */
export function offsetLabel(instant: string | Date, zone: string): string {
  const total = offsetMinutes(instant, zone);
  if (total === 0) return "UTC";
  const abs = Math.abs(total);
  const minutes = abs % 60;
  return `UTC${total < 0 ? "-" : "+"}${String(Math.floor(abs / 60))}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}`;
}

export interface ZonedDisplay {
  /** In the zone: "8 Oct 2026, 08:40". */
  local: string;
  /** "06:40 UTC" (same day) or "7 Oct 2026, 23:40 UTC" (another calendar day than `local`). */
  utc: string;
  zone: string;
  /** The zone's offset at that instant, "UTC+2". */
  offset: string;
  /** False when the zone shows UTC already (no second time is needed). */
  showUtc: boolean;
}

function display(instant: string | Date, zone: string, opts: FormatOptions): ZonedDisplay {
  const d = parseInstant(instant);
  const local = formatInZone(d, zone, opts);
  const offset = offsetLabel(d, zone);
  const sameDay =
    new Intl.DateTimeFormat(formatLocale(), { timeZone: zone, dateStyle: "short" }).format(d) ===
    new Intl.DateTimeFormat(formatLocale(), { timeZone: "UTC", dateStyle: "short" }).format(d);
  const utcTime = new Intl.DateTimeFormat(formatLocale(), {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    ...(opts.seconds ? { second: "2-digit" } : {}),
    hourCycle: "h23",
  }).format(d);
  return {
    local,
    utc: sameDay ? `${utcTime} UTC` : `${formatInZone(d, "UTC", opts)} UTC`,
    zone,
    offset,
    // A zero offset (UTC, Etc/UTC, GMT, …) shows UTC already: no second time.
    showUtc: offsetMinutes(d, zone) !== 0,
  };
}

/** A time on a screen: the viewer's zone next to UTC. */
export function screenTime(instant: string | Date, viewer: string, opts: FormatOptions = {}): ZonedDisplay {
  return display(instant, viewer, opts);
}

/** A time inside a document (or a document's deadline shown on screen): the company zone next to UTC. */
export function documentTime(instant: string | Date, company: string, opts: FormatOptions = {}): ZonedDisplay {
  return display(instant, company, opts);
}

/**
 * True when a screen in the viewer's zone shows a document in another zone: the screen must then say
 * "Documents use the company time zone, <zone>" (04 §11).
 */
export function documentZoneDiffers(viewer: string, company: string): boolean {
  return viewer !== company;
}
